import React, { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, X, CheckCircle2 } from "lucide-react";

// Beep curto usando WebAudio — funciona em qualquer dispositivo moderno sem
// precisar carregar um arquivo de áudio. Frequência clara que imita o "bip"
// de leitores de supermercado.
function playBeep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = 1080;
    gain.gain.value = 0.08;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.12);
    osc.onended = () => { try { ctx.close(); } catch {} };
  } catch {}
}

/**
 * Scanner reutilizável de código de barras / QR code.
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - onDetect: (code: string) => void  // chamado a cada leitura
 * - continuous: boolean (default false) — mantém a câmera aberta após detectar
 *               para leituras múltiplas (usado no fluxo de venda).
 * - title?: string
 */
export default function BarcodeScanner({ open, onClose, onDetect, continuous = false, title }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const lastCodeRef = useRef({ value: "", at: 0 });
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(null);
  const [error, setError] = useState(null);
  const [lastDetected, setLastDetected] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setLastDetected(null);

    (async () => {
      try {
        // Lista as câmeras disponíveis
        const list = await BrowserMultiFormatReader.listVideoInputDevices();
        if (cancelled) return;
        setDevices(list);
        // Preferência: câmera traseira ("environment", "back", "rear")
        const back = list.find(d => /back|rear|environment|tras|fund/i.test(d.label));
        const chosen = back?.deviceId || (list[0] && list[0].deviceId) || null;
        setDeviceId(chosen);
        await startScanner(chosen);
      } catch (e) {
        setError(e?.message || "Não foi possível acessar a câmera");
      }
    })();

    return () => {
      cancelled = true;
      stopScanner();
    };
    // eslint-disable-next-line
  }, [open]);

  const startScanner = async (chosenDeviceId) => {
    stopScanner();
    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;
    try {
      const controls = await reader.decodeFromVideoDevice(
        chosenDeviceId || null,
        videoRef.current,
        (result, err) => {
          if (result) {
            const text = result.getText();
            const now = Date.now();
            // dedup: ignora se o mesmo código foi lido nos últimos 1500ms
            if (lastCodeRef.current.value === text && now - lastCodeRef.current.at < 1500) return;
            lastCodeRef.current = { value: text, at: now };
            playBeep();
            setLastDetected(text);
            try { onDetect && onDetect(text); } catch {}
            if (!continuous) {
              setTimeout(() => { stopScanner(); onClose && onClose(); }, 250);
            }
          }
        }
      );
      controlsRef.current = controls;
    } catch (e) {
      setError(e?.message || "Erro ao iniciar a câmera");
    }
  };

  const stopScanner = () => {
    try { controlsRef.current && controlsRef.current.stop(); } catch {}
    controlsRef.current = null;
    readerRef.current = null;
  };

  const switchCamera = async (id) => {
    setDeviceId(id);
    await startScanner(id);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { stopScanner(); onClose && onClose(); } }}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="barcode-scanner-dialog">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Camera size={16} /> {title || "Ler código"}
          </DialogTitle>
        </DialogHeader>

        <div className="relative bg-black aspect-[3/4] w-full overflow-hidden">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted
            data-testid="barcode-scanner-video"
          />
          {/* Overlay: linha central + retângulo de mira */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-x-6 top-1/2 -translate-y-1/2 h-[36%] border-2 border-white/70 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]">
              <div className="absolute left-2 right-2 top-1/2 h-px bg-red-500 shadow-[0_0_8px_2px_rgba(239,68,68,0.7)]"/>
            </div>
            <p className="absolute top-3 left-0 right-0 text-center text-white text-xs px-4">
              Posicione o código dentro da moldura
            </p>
            {lastDetected && (
              <div className="absolute bottom-3 left-3 right-3 bg-emerald-600/95 text-white text-sm rounded-md px-3 py-2 flex items-center gap-2 animate-in fade-in zoom-in-95">
                <CheckCircle2 size={16}/> <span className="truncate">{lastDetected}</span>
              </div>
            )}
          </div>
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/90 text-white p-6 text-center text-sm">
              <div>
                <div className="font-medium mb-1">Câmera indisponível</div>
                <div className="text-xs text-white/70">{error}</div>
              </div>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-[#E5E5E5] flex items-center gap-2 flex-wrap">
          {devices.length > 1 && (
            <select
              value={deviceId || ""}
              onChange={(e) => switchCamera(e.target.value)}
              className="text-xs border rounded px-2 py-1.5 flex-1"
              data-testid="barcode-scanner-camera-select"
            >
              {devices.map(d => (
                <option key={d.deviceId} value={d.deviceId}>{d.label || `Câmera ${d.deviceId.slice(0, 6)}`}</option>
              ))}
            </select>
          )}
          <Button
            variant="outline"
            onClick={() => { stopScanner(); onClose && onClose(); }}
            className="ml-auto"
            data-testid="barcode-scanner-close"
          >
            <X size={14} className="mr-1"/> {continuous ? "Encerrar" : "Cancelar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
